import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Image as ImageIcon, Smile, Bold, Italic,
    Strikethrough, Code, Plus, X, UploadCloud,
    MapPin, Video, FileText, Info, ChevronDown, Loader2
} from 'lucide-react';
import styles from './CreateTemplate.module.css';

const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001').replace(/\/api\/v1(?:\/public)?\/?$/, '').replace(/\/+$/, '');
const TEMPLATE_BASE_API = `${API_ORIGIN}/api/v1/smiify/sm/templates`;

const CATEGORIES = [
    { value: 'MARKETING', label: 'Marketing', desc: 'Promotions, offers, announcements or invitations for customers.' },
    { value: 'UTILITY', label: 'Utility', desc: 'Updates about a specific transaction, account, order or subscription.' },
    { value: 'AUTHENTICATION', label: 'Authentication', desc: 'One-time passcodes for verifying a login or transaction.' }
];

const LANGUAGES = [
    { code: 'en_US', label: 'English (US)' }, { code: 'en_GB', label: 'English (UK)' },
    { code: 'en', label: 'English' }, { code: 'hi', label: 'Hindi' },
    { code: 'es', label: 'Spanish' }, { code: 'es_AR', label: 'Spanish (Argentina)' },
    { code: 'pt_BR', label: 'Portuguese (Brazil)' }, { code: 'ar', label: 'Arabic' },
    { code: 'fr', label: 'French' }, { code: 'id', label: 'Indonesian' }, { code: 'de', label: 'German' }
];

const COUNTRY_CODES = ['+91', '+1', '+44', '+61', '+971', '+65', '+81'];
const BUTTON_TYPES = ['Custom', 'Visit website', 'Call phone number', 'Copy code', 'Autofill'];
const BUTTON_LIMITS = { 'Custom': 3, 'Visit website': 2, 'Call phone number': 1, 'Copy code': 1, 'Autofill': 1 };
const MAX_TOTAL_BUTTONS = 6;

const emptyButtonFor = (type) => {
    switch (type) {
        case 'Custom': return { type, text: 'Quick Reply' };
        case 'Visit website': return { type, text: 'Visit Website', urlType: 'Static', url: '', urlExample: '' };
        case 'Call phone number': return { type, text: 'Call Us', phoneCountry: '+91', phoneNumber: '' };
        case 'Copy code': return { type, text: 'Copy Code', sampleValue: '' };
        case 'Autofill': return { type, text: 'Autofill', packageName: '', signatureHash: '' };
        default: return { type, text: '' };
    }
};

const tokenFor = (variableType, position, name) => variableType === 'NUMBER' ? `{{${position + 1}}}` : `{{${name}}}`;
const sanitizeVarName = (raw) => raw.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/^_+/, '') || 'variable';

const CreateTemplate = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    const isEditMode = !!id;

    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);

    const [formData, setFormData] = useState({
        category: 'MARKETING',
        name: 'your_template_name',
        language: 'en_US',
        variableType: 'NUMBER',
        headerType: 'None',
        headerText: '',
        headerVariableActive: false,
        headerVariable: { name: 'header_variable', value: '' },
        headerMedia: null,
        headerMediaUrl: null,
        headerMediaHandle: null,
        bodyText: 'Hello {{1}}, your order #{{2}} has been shipped!',
        bodyVariables: [{ name: 'customer_name', value: 'John' }, { name: 'order_id', value: '54321' }],
        footerText: '',
        buttons: []
    });

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isVarTypeOpen, setIsVarTypeOpen] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 🚀 EDIT MODE PARSER
    useEffect(() => {
        if (isEditMode && location.state?.template) {
            const tpl = location.state.template;

            // 🚀 BINGO FIX: Kick out users trying to edit Default Meta Templates
            if (tpl.name.includes('jaspers_market') || tpl.name.includes('hello_world')) {
                alert("🔒 You cannot edit Meta's built-in sample templates. Please create your own.");
                navigate('/marketing/templates');
                return;
            }
            if (tpl.status === 'PENDING') {
                alert("⏳ This template is currently PENDING review by Meta. You cannot edit it until it is Approved or Rejected.");
                navigate('/marketing/templates');
                return;
            }

            const prefilledForm = {
                name: tpl.name || '',
                category: tpl.category || 'MARKETING',
                language: tpl.lang || 'en_US',
                variableType: 'NUMBER',
                headerType: 'None',
                headerText: '',
                headerVariableActive: false,
                headerVariable: { name: 'header_variable', value: '' },
                headerMedia: null,
                headerMediaUrl: null,
                headerMediaHandle: null,
                bodyText: '',
                bodyVariables: [],
                footerText: '',
                buttons: []
            };

            if (tpl.components && Array.isArray(tpl.components)) {
                tpl.components.forEach(comp => {
                    if (comp.type === 'HEADER') {
                        if (comp.format === 'TEXT') {
                            prefilledForm.headerType = 'Text';
                            prefilledForm.headerText = comp.text || '';
                            if (comp.example?.header_text?.length > 0) {
                                prefilledForm.headerVariableActive = true;
                                prefilledForm.headerVariable = { name: 'header_variable', value: comp.example.header_text[0] };
                            }
                        } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(comp.format)) {
                            prefilledForm.headerType = comp.format.charAt(0) + comp.format.slice(1).toLowerCase();
                            const mediaVal = comp.example?.header_handle?.[0] || comp.example?.header_url?.[0];
                            if (mediaVal) {
                                prefilledForm.headerMediaHandle = mediaVal;
                                if (mediaVal.startsWith('http') || mediaVal.startsWith('blob:')) {
                                    prefilledForm.headerMediaUrl = mediaVal;
                                }
                            }
                        } else if (comp.format === 'LOCATION') {
                            prefilledForm.headerType = 'Location';
                        }
                    }
                    else if (comp.type === 'BODY') {
                        prefilledForm.bodyText = comp.text || '';
                        const matches = comp.text?.match(/\{\{.*?\}\}/g);
                        if (matches) {
                            let examplesList = [];
                            if (comp.example?.body_text) {
                                examplesList = comp.example.body_text[0] || [];
                            } else if (comp.example?.body_text_named_params) {
                                examplesList = comp.example.body_text_named_params.map(p => p.example);
                            }
                            prefilledForm.bodyVariables = matches.map((m, i) => ({
                                name: `variable_${i + 1}`,
                                value: examplesList[i] || `Sample_${i + 1}`
                            }));
                        }
                    }
                    else if (comp.type === 'FOOTER') {
                        prefilledForm.footerText = comp.text || '';
                    }
                    else if (comp.type === 'BUTTONS') {
                        prefilledForm.buttons = comp.buttons.map(b => {
                            if (b.type === 'QUICK_REPLY') return { type: 'Custom', text: b.text };
                            if (b.type === 'URL') return {
                                type: 'Visit website',
                                text: b.text,
                                urlType: b.url?.includes('{{') ? 'Dynamic' : 'Static',
                                url: b.url || '',
                                urlExample: b.example?.[0] || ''
                            };
                            if (b.type === 'PHONE_NUMBER') {
                                let extractedCode = '+91';
                                let extractedNum = b.phone_number || '';
                                const sortedCodes = [...COUNTRY_CODES].sort((x, y) => y.length - x.length);
                                for (const c of sortedCodes) {
                                    if (extractedNum.startsWith(c)) {
                                        extractedCode = c;
                                        extractedNum = extractedNum.substring(c.length);
                                        break;
                                    } else if (extractedNum.startsWith(c.replace('+', ''))) {
                                        extractedCode = c;
                                        extractedNum = extractedNum.substring(c.length - 1);
                                        break;
                                    }
                                }
                                return { type: 'Call phone number', text: b.text, phoneCountry: extractedCode, phoneNumber: extractedNum };
                            }
                            if (b.type === 'COPY_CODE') return { type: 'Copy code', text: 'Copy Code', sampleValue: b.example?.[0] || '' };
                            return null;
                        }).filter(Boolean);
                    }
                });
            }
            setFormData(prev => ({ ...prev, ...prefilledForm }));
        }
    }, [isEditMode, location.state, navigate]);

    const headerHasVariable = formData.headerType === 'Text' && formData.headerVariableActive;
    const buttonTypeCounts = useMemo(() => {
        const counts = {};
        formData.buttons.forEach((b) => { counts[b.type] = (counts[b.type] || 0) + 1; });
        return counts;
    }, [formData.buttons]);
    const totalButtons = formData.buttons.length;

    const bodyValueMap = useMemo(() => {
        const map = {};
        formData.bodyVariables.forEach((v, idx) => {
            const key = formData.variableType === 'NUMBER' ? String(idx + 1) : v.name;
            map[key] = v.value;
        });
        return map;
    }, [formData.bodyVariables, formData.variableType]);

    const headerValueMap = useMemo(() => {
        if (!headerHasVariable) return {};
        const key = formData.variableType === 'NUMBER' ? '1' : formData.headerVariable.name;
        return { [key]: formData.headerVariable.value };
    }, [headerHasVariable, formData.headerVariable, formData.variableType]);

    const getValidationErrors = () => {
        const errors = [];
        if (!formData.name.trim()) errors.push('Template name is required.');
        if (!formData.bodyText.trim()) errors.push('Body text is required.');

        formData.bodyVariables.forEach((v, idx) => {
            if (formData.variableType === 'NAME' && !v.name.trim()) errors.push(`Give variable #${idx + 1} a name.`);
            if (!v.value.trim()) errors.push(`Give variable #${idx + 1} a sample value.`);
        });

        if (formData.headerType === 'Text') {
            if (!formData.headerText.trim()) errors.push('Header text cannot be empty.');
            if (headerHasVariable && !formData.headerVariable.value.trim()) errors.push('Give the header variable a sample value.');
        }

        if (['Image', 'Video', 'Document'].includes(formData.headerType) && !formData.headerMedia && !formData.headerMediaHandle) {
            errors.push(`Upload a sample ${formData.headerType.toLowerCase()} to save this template.`);
        }

        formData.buttons.forEach((btn) => {
            if (!btn.text.trim()) { errors.push('Every button needs text.'); return; }
            if (btn.type === 'Visit website') {
                if (!btn.url.trim()) errors.push('Add URL.');
                if (btn.urlType === 'Dynamic' && !btn.urlExample.trim()) errors.push('Add dynamic sample URL.');
            }
            if (btn.type === 'Call phone number' && !btn.phoneNumber.trim()) errors.push('Add a phone number.');
        });
        return errors;
    };

    const validationErrors = getValidationErrors();
    const isTemplateValid = validationErrors.length === 0;

    const handleChange = (e) => {
        let value = e.target.value;
        if (e.target.name === 'name') value = value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
        setFormData((prev) => ({ ...prev, [e.target.name]: value }));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prev) => ({ ...prev, headerMedia: file, headerMediaUrl: URL.createObjectURL(file) }));
        }
        e.target.value = null;
    };

    const applyFormatting = (formatChar) => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = formData.bodyText;
        const selectedText = text.substring(start, end);
        const newText = text.substring(0, start) + formatChar + selectedText + formatChar + text.substring(end);
        setFormData((prev) => ({ ...prev, bodyText: newText }));
        setTimeout(() => { textarea.focus(); textarea.setSelectionRange(start + formatChar.length, end + formatChar.length); }, 0);
    };

    const switchVariableType = (newType) => {
        setIsVarTypeOpen(false);
        if (newType === formData.variableType) return;
        let newBodyText = formData.bodyText;
        const newBodyVariables = formData.bodyVariables.map((v, idx) => {
            const oldToken = tokenFor(formData.variableType, idx, v.name);
            const safeName = v.name && v.name.trim() ? sanitizeVarName(v.name) : `variable_${idx + 1}`;
            const newToken = tokenFor(newType, idx, safeName);
            if (oldToken !== newToken) newBodyText = newBodyText.split(oldToken).join(newToken);
            return { ...v, name: safeName };
        });
        setFormData((prev) => ({ ...prev, variableType: newType, bodyText: newBodyText, bodyVariables: newBodyVariables }));
    };

    const handleAddVariable = () => {
        const position = formData.bodyVariables.length;
        const defaultName = `variable_${position + 1}`;
        const token = tokenFor(formData.variableType, position, defaultName);
        setFormData((prev) => ({ ...prev, bodyVariables: [...prev.bodyVariables, { name: defaultName, value: '' }], bodyText: prev.bodyText + token }));
    };

    const updateBodyVariableValue = (idx, value) => {
        setFormData((prev) => {
            const updated = [...prev.bodyVariables];
            updated[idx] = { ...updated[idx], value };
            return { ...prev, bodyVariables: updated };
        });
    };

    const renameBodyVariable = (idx, rawName) => {
        const safeName = sanitizeVarName(rawName);
        const oldToken = tokenFor(formData.variableType, idx, formData.bodyVariables[idx].name);
        const newToken = tokenFor(formData.variableType, idx, safeName);
        const newBodyText = formData.bodyText.split(oldToken).join(newToken);
        setFormData((prev) => {
            const updated = [...prev.bodyVariables];
            updated[idx] = { ...updated[idx], name: safeName };
            return { ...prev, bodyText: newBodyText, bodyVariables: updated };
        });
    };

    const removeBodyVariable = (idx) => {
        const removedToken = tokenFor(formData.variableType, idx, formData.bodyVariables[idx].name);
        const remaining = formData.bodyVariables.filter((_, i) => i !== idx);
        setFormData((prev) => ({ ...prev, bodyText: prev.bodyText.split(removedToken).join(''), bodyVariables: remaining }));
    };

    const handleAddHeaderVariable = () => {
        if (formData.headerVariableActive) return;
        const defaultName = 'header_variable';
        const token = tokenFor(formData.variableType, 0, defaultName);
        setFormData((prev) => ({ ...prev, headerVariableActive: true, headerVariable: { name: defaultName, value: '' }, headerText: `${prev.headerText}${token}` }));
    };

    const removeHeaderVariable = () => {
        const token = tokenFor(formData.variableType, 0, formData.headerVariable.name);
        setFormData((prev) => ({ ...prev, headerVariableActive: false, headerText: prev.headerText.split(token).join(''), headerVariable: { name: 'header_variable', value: '' } }));
    };

    const renameHeaderVariable = (rawName) => {
        const safeName = sanitizeVarName(rawName);
        const oldToken = tokenFor(formData.variableType, 0, formData.headerVariable.name);
        const newToken = tokenFor(formData.variableType, 0, safeName);
        setFormData((prev) => ({ ...prev, headerText: prev.headerText.split(oldToken).join(newToken), headerVariable: { ...prev.headerVariable, name: safeName } }));
    };

    const updateHeaderVariableValue = (value) => {
        setFormData((prev) => ({ ...prev, headerVariable: { ...prev.headerVariable, value } }));
    };

    const canAddButtonType = (type) => {
        if (totalButtons >= MAX_TOTAL_BUTTONS) return false;
        const cap = BUTTON_LIMITS[type] ?? 1;
        return (buttonTypeCounts[type] || 0) < cap;
    };

    const addActionBtn = (type) => {
        if (!canAddButtonType(type)) return;
        setFormData((prev) => ({ ...prev, buttons: [...prev.buttons, emptyButtonFor(type)] }));
        setIsMenuOpen(false);
    };

    const updateButton = (index, field, value) => {
        setFormData((prev) => {
            const updated = [...prev.buttons];
            updated[index] = { ...updated[index], [field]: value };
            return { ...prev, buttons: updated };
        });
    };

    const removeButton = (index) => {
        setFormData((prev) => ({ ...prev, buttons: prev.buttons.filter((_, i) => i !== index) }));
    };

    const handleSubmit = async () => {
        setShowErrors(true);
        if (!isTemplateValid) return;

        setIsSubmitting(true);
        try {
            const token = localStorage.getItem('smiify_token');
            const activeStoreId = localStorage.getItem('smiify_active_store_id');

            let headerMediaBase64 = null;
            let headerMediaName = null;
            let headerMediaType = null;

            if (formData.headerMedia) {
                headerMediaName = formData.headerMedia.name;
                headerMediaType = formData.headerMedia.type;
                headerMediaBase64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = error => reject(error);
                    reader.readAsDataURL(formData.headerMedia);
                });
            }

            // 🚀 SMART PARSER: Automatically convert UI "Named" variables to Meta's {{1}} standard format
            let safeBodyText = formData.bodyText;
            let safeHeaderText = formData.headerText;

            if (formData.variableType === 'NAME') {
                formData.bodyVariables.forEach((v, idx) => {
                    safeBodyText = safeBodyText.split(`{{${v.name}}}`).join(`{{${idx + 1}}}`);
                });
                if (formData.headerVariableActive && formData.headerVariable.name) {
                    safeHeaderText = safeHeaderText.split(`{{${formData.headerVariable.name}}}`).join(`{{1}}`);
                }
            }

            const payloadToSubmit = {
                ...formData,
                bodyText: safeBodyText,
                headerText: safeHeaderText,
                variableType: 'NUMBER',
                headerMediaBase64,
                headerMediaName,
                headerMediaType
            };

            delete payloadToSubmit.headerMedia;
            delete payloadToSubmit.headerMediaUrl;

            const ENDPOINT = isEditMode ? `${TEMPLATE_BASE_API}/edit/${id}` : `${TEMPLATE_BASE_API}/create`;

            const res = await fetch(ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'x-store-id': activeStoreId || 'global_hq'
                },
                body: JSON.stringify(payloadToSubmit)
            });

            const json = await res.json();

            if (json.success) {
                alert(isEditMode ? 'Template updated successfully!' : 'Template successfully sent to Meta for review!');
                navigate('/marketing/templates');
            } else {
                alert(`Error from Meta: ${json.message}`);
            }
        } catch (error) {
            alert('Failed to connect to the server. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderBodyPreview = (text, valueMap) => {
        if (!text) return 'Your message...';
        let htmlText = text
            .replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/\*(.*?)\*/gs, '<strong>$1</strong>')
            .replace(/_(.*?)_/gs, '<em>$1</em>')
            .replace(/~(.*?)~/gs, '<del>$1</del>')
            .replace(/```(.*?)```/gs, '<code>$1</code>')
            .replace(/\n/g, '<br/>');
        htmlText = htmlText.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (match, key) => {
            const value = valueMap && valueMap[key];
            return `<span class="${styles.waVariable}">${value ? value : match}</span>`;
        });
        return <div dangerouslySetInnerHTML={{ __html: htmlText }} />;
    };

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.mainContent}>

                {/* LEFT PANEL */}
                <div className={styles.leftPanel}>
                    <div className={styles.statusCard}>
                        <div className={styles.statusInfo}>
                            <h2>{isEditMode ? 'Editing:' : ''} {formData.name || 'your_template_name'} • {LANGUAGES.find((l) => l.code === formData.language)?.label}</h2>
                            <p>{CATEGORIES.find((c) => c.value === formData.category)?.label} • Default</p>
                        </div>
                    </div>

                    <div className={styles.formCard}>
                        <h3 className={styles.cardTitle}>Category</h3>
                        <p className={styles.cardSubtitle}>Choose the category that best describes this template.</p>
                        <div className={styles.categoryGrid}>
                            {CATEGORIES.map((cat) => (
                                <button
                                    key={cat.value}
                                    type="button"
                                    disabled={isEditMode}
                                    className={`${styles.categoryCard} ${formData.category === cat.value ? styles.active : ''} ${isEditMode ? styles.disabledBtn : ''}`}
                                    onClick={() => setFormData((prev) => ({ ...prev, category: cat.value }))}
                                >
                                    <p className={styles.categoryCardTitle}>{cat.label}</p>
                                    <p className={styles.categoryCardDesc}>{cat.desc}</p>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.formCard}>
                        <h3 className={styles.cardTitle}>Template name and language</h3>
                        <div className={styles.formRow}>
                            <div className={styles.formGroup} style={{ flex: 2 }}>
                                <label>Name your template</label>
                                <div className={styles.inputWrapper}>
                                    <input type="text" name="name" className={styles.formInput} placeholder="enter_template_name" value={formData.name} onChange={handleChange} maxLength={512} disabled={isEditMode} style={{ backgroundColor: isEditMode ? '#f1f5f9' : '#fff' }} />
                                    <span className={styles.charCount}>{formData.name.length}/512</span>
                                </div>
                            </div>
                            <div className={styles.formGroup} style={{ flex: 1 }}>
                                <label>Language</label>
                                <select name="language" className={styles.formSelect} value={formData.language} onChange={handleChange} disabled={isEditMode} style={{ backgroundColor: isEditMode ? '#f1f5f9' : '#fff' }}>
                                    {LANGUAGES.map((lang) => (
                                        <option key={lang.code} value={lang.code}>{lang.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {isEditMode && <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '8px' }}>* Name, Language, and Category cannot be changed while editing.</p>}
                    </div>

                    <div className={styles.formCard}>
                        <h3 className={styles.cardTitle}>Content</h3>
                        <p className={styles.cardSubtitle}>Add a header, body and footer for your template.</p>

                        <div className={styles.varTypeWrapper}>
                            <div className={styles.varTypeLabelRow}>
                                Type of variable
                                <span className={styles.varTypeInfoIcon} title="Number uses {{1}}, {{2}}... placeholders. Name uses readable placeholders like {{customer_name}}."><Info size={14} /></span>
                            </div>
                            <button type="button" className={`${styles.varTypeTrigger} ${isVarTypeOpen ? styles.open : ''}`} onClick={() => setIsVarTypeOpen(!isVarTypeOpen)}>
                                {formData.variableType === 'NAME' ? 'Name' : 'Number'}
                                <ChevronDown size={16} className={`${styles.varTypeChevron} ${isVarTypeOpen ? styles.open : ''}`} />
                            </button>
                            {isVarTypeOpen && (
                                <div className={styles.varTypeMenu}>
                                    <button type="button" className={`${styles.varTypeOption} ${formData.variableType === 'NAME' ? styles.selected : ''}`} onClick={() => switchVariableType('NAME')}>
                                        <span className={`${styles.varRadio} ${formData.variableType === 'NAME' ? styles.selected : ''}`}>{formData.variableType === 'NAME' && <span className={styles.varRadioDot} />}</span> Name
                                    </button>
                                    <button type="button" className={`${styles.varTypeOption} ${formData.variableType === 'NUMBER' ? styles.selected : ''}`} onClick={() => switchVariableType('NUMBER')}>
                                        <span className={`${styles.varRadio} ${formData.variableType === 'NUMBER' ? styles.selected : ''}`}>{formData.variableType === 'NUMBER' && <span className={styles.varRadioDot} />}</span> Number
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Header */}
                        <div className={styles.formGroup} style={{ marginBottom: '20px' }}>
                            <label>Header <span className={styles.optionalText}>• Optional</span></label>

                            <div className={styles.formRow} style={{ marginTop: '8px', marginBottom: '0' }}>
                                <div className={styles.formGroup} style={{ flex: 1 }}>
                                    <select
                                        className={styles.formSelect}
                                        value={formData.headerType}
                                        onChange={(e) => setFormData((prev) => ({
                                            ...prev, headerType: e.target.value, headerMedia: null, headerMediaUrl: null,
                                            headerText: '', headerVariableActive: false, headerVariable: { name: 'header_variable', value: '' }
                                        }))}
                                    >
                                        <option value="None">None</option>
                                        <option value="Text">Text</option>
                                        <option value="Image">Image</option>
                                        <option value="Video">Video</option>
                                        <option value="Document">Document</option>
                                        <option value="Location">Location</option>
                                    </select>
                                </div>

                                {formData.headerType === 'Text' && (
                                    <div className={styles.formGroup} style={{ flex: 2 }}>
                                        <div className={styles.inputWrapper}>
                                            <input type="text" name="headerText" className={styles.formInput} placeholder="Add a short text header" value={formData.headerText} onChange={handleChange} maxLength={60} />
                                            <span className={styles.charCount}>{formData.headerText.length}/60</span>
                                        </div>
                                    </div>
                                )}

                                {['Image', 'Video', 'Document'].includes(formData.headerType) && (
                                    <div className={styles.formGroup} style={{ flex: 2 }}>
                                        <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />

                                        {!formData.headerMedia ? (
                                            <div className={styles.uploadZone} onClick={() => fileInputRef.current.click()}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <UploadCloud size={18} color="#008069" />
                                                    <p>{isEditMode && formData.headerMediaHandle ? 'Current Media Active (Click to Replace)' : `Upload ${formData.headerType}`}</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={styles.fileBadge} onClick={() => fileInputRef.current.click()} style={{ cursor: 'pointer' }}>
                                                <span>{formData.headerMedia.name}</span>
                                                <button type="button" className={styles.removeFile} onClick={(e) => { e.stopPropagation(); setFormData((prev) => ({ ...prev, headerMedia: null, headerMediaUrl: null })) }}><X size={16} /></button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {formData.headerType === 'Location' && (
                                    <div className={styles.formGroup} style={{ flex: 2 }}>
                                        <div className={styles.locationNote}>
                                            <span>The location is sent dynamically with each message.</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {formData.headerType === 'Text' && (
                                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <button type="button" className={styles.addVarBtn} onClick={handleAddHeaderVariable} disabled={headerHasVariable}>
                                        <Plus size={14} /> Add variable
                                    </button>
                                    {headerHasVariable && <span className={styles.fieldHint}>Header supports a single variable.</span>}
                                </div>
                            )}

                            {headerHasVariable && (
                                <div className={styles.variableExampleBox}>
                                    <p className={styles.variableExampleTitle}>Header variable</p>
                                    <div className={styles.variableExampleRow}>
                                        {formData.variableType === 'NUMBER' ? (
                                            <span className={styles.variableTag}>{'{{1}}'}</span>
                                        ) : (
                                            <div className={styles.variableFieldCol}>
                                                <span className={styles.variableFieldLabel}>Name</span>
                                                <input type="text" className={styles.formInput} placeholder="e.g. sale_name"
                                                    value={formData.headerVariable.name}
                                                    onChange={(e) => renameHeaderVariable(e.target.value)} />
                                            </div>
                                        )}
                                        <div className={styles.variableFieldCol}>
                                            <span className={styles.variableFieldLabel}>Value</span>
                                            <input type="text" className={styles.formInput} placeholder="e.g. Diwali Sale"
                                                value={formData.headerVariable.value}
                                                onChange={(e) => updateHeaderVariableValue(e.target.value)} />
                                        </div>
                                        <button type="button" className={styles.variableRemoveBtn} onClick={removeHeaderVariable}><X size={16} /></button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Body */}
                        <div className={styles.formGroup} style={{ marginBottom: '20px' }}>
                            <label>Body</label>
                            <div className={styles.bodyEditor}>
                                <textarea
                                    ref={textareaRef}
                                    name="bodyText"
                                    className={styles.bodyTextarea}
                                    value={formData.bodyText}
                                    onChange={handleChange}
                                    maxLength={1024}
                                />
                                <div className={styles.bodyToolbar}>
                                    <div className={styles.toolbarGroup}>
                                        <button type="button" className={styles.toolBtn} title="Emoji"><Smile size={16} /></button>
                                        <button type="button" className={styles.toolBtn} title="Bold" onClick={() => applyFormatting('*')}><Bold size={16} /></button>
                                        <button type="button" className={styles.toolBtn} title="Italic" onClick={() => applyFormatting('_')}><Italic size={16} /></button>
                                        <button type="button" className={styles.toolBtn} title="Strikethrough" onClick={() => applyFormatting('~')}><Strikethrough size={16} /></button>
                                        <button type="button" className={styles.toolBtn} title="Monospace" onClick={() => applyFormatting('```')}><Code size={16} /></button>
                                    </div>
                                    <div className={styles.toolbarGroup}>
                                        <button type="button" className={styles.addVarBtn} onClick={handleAddVariable}><Plus size={14} /> Add variable</button>
                                        <div style={{ fontSize: '12px', color: '#8696a0', paddingLeft: '12px', borderLeft: '1px solid #d1d7db' }}>{formData.bodyText.length}/1024</div>
                                    </div>
                                </div>
                            </div>

                            {formData.bodyVariables.length > 0 && (
                                <div className={styles.variableExampleBox}>
                                    <p className={styles.variableExampleTitle}>
                                        Body variables <span className={styles.optionalText}>(required)</span>
                                    </p>
                                    {formData.bodyVariables.map((v, idx) => (
                                        <div className={styles.variableExampleRow} key={idx}>
                                            {formData.variableType === 'NUMBER' ? (
                                                <span className={styles.variableTag}>{`{{${idx + 1}}}`}</span>
                                            ) : (
                                                <div className={styles.variableFieldCol}>
                                                    <span className={styles.variableFieldLabel}>Name</span>
                                                    <input type="text" className={styles.formInput} placeholder="e.g. customer_name"
                                                        value={v.name} onChange={(e) => renameBodyVariable(idx, e.target.value)} />
                                                </div>
                                            )}
                                            <div className={styles.variableFieldCol}>
                                                <span className={styles.variableFieldLabel}>Value</span>
                                                <input type="text" className={styles.formInput} placeholder="e.g. John"
                                                    value={v.value} onChange={(e) => updateBodyVariableValue(idx, e.target.value)} />
                                            </div>
                                            <button type="button" className={styles.variableRemoveBtn} onClick={() => removeBodyVariable(idx)}><X size={16} /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className={styles.formGroup}>
                            <label>Footer <span className={styles.optionalText}>• Optional</span></label>
                            <div className={styles.inputWrapper} style={{ marginTop: '8px' }}>
                                <input type="text" name="footerText" className={styles.formInput} placeholder="Add a short line of text to the bottom" value={formData.footerText} onChange={handleChange} maxLength={60} />
                            </div>
                        </div>
                    </div>

                    {/* Buttons */}
                    <div className={styles.formCard}>
                        <h3 className={styles.cardTitle}>Buttons <span className={styles.optionalText}>• Optional</span></h3>

                        {formData.buttons.length > 0 && (
                            <div className={styles.buttonList}>
                                {formData.buttons.map((btn, idx) => (
                                    <div key={idx} className={styles.buttonItem}>
                                        <div className={styles.buttonItemHeader}>
                                            <span className={styles.buttonTypeLabel}>{btn.type}</span>
                                            <input type="text" className={styles.formInput} placeholder="Button text" maxLength={25}
                                                value={btn.text} onChange={(e) => updateButton(idx, 'text', e.target.value)} />
                                            <button type="button" className={styles.removeBtn} onClick={() => removeButton(idx)}><X size={18} /></button>
                                        </div>

                                        {btn.type === 'Visit website' && (
                                            <div className={styles.buttonSubRow}>
                                                <div className={styles.segmentedControl}>
                                                    <button type="button" className={`${styles.segmentedOption} ${btn.urlType === 'Static' ? styles.active : ''}`}
                                                        onClick={() => updateButton(idx, 'urlType', 'Static')}>Static</button>
                                                    <button type="button" className={`${styles.segmentedOption} ${btn.urlType === 'Dynamic' ? styles.active : ''}`}
                                                        onClick={() => updateButton(idx, 'urlType', 'Dynamic')}>Dynamic</button>
                                                </div>
                                                <input type="text" className={styles.formInput} style={{ flex: 1, minWidth: 160 }}
                                                    placeholder={btn.urlType === 'Dynamic' ? 'https://example.com/{{1}}' : 'https://example.com'}
                                                    value={btn.url} onChange={(e) => updateButton(idx, 'url', e.target.value)} />
                                                {btn.urlType === 'Dynamic' && (
                                                    <input type="text" className={styles.formInput} style={{ flex: 1, minWidth: 140 }}
                                                        placeholder="Sample suffix, e.g. order/12345"
                                                        value={btn.urlExample} onChange={(e) => updateButton(idx, 'urlExample', e.target.value)} />
                                                )}
                                            </div>
                                        )}

                                        {btn.type === 'Call phone number' && (
                                            <div className={styles.buttonSubRow}>
                                                <select className={`${styles.formSelect} ${styles.countrySelect}`}
                                                    value={btn.phoneCountry} onChange={(e) => updateButton(idx, 'phoneCountry', e.target.value)}>
                                                    {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                                <input type="tel" className={`${styles.formInput} ${styles.phoneInput}`} placeholder="Phone number"
                                                    value={btn.phoneNumber} onChange={(e) => updateButton(idx, 'phoneNumber', e.target.value)} />
                                            </div>
                                        )}

                                        {btn.type === 'Copy code' && (
                                            <div className={styles.buttonSubRow}>
                                                <input type="text" className={styles.formInput} style={{ flex: 1 }} placeholder="Sample value, e.g. SAVE20 or a verification code"
                                                    value={btn.sampleValue} onChange={(e) => updateButton(idx, 'sampleValue', e.target.value)} />
                                            </div>
                                        )}

                                        {btn.type === 'Autofill' && (
                                            <div className={styles.buttonSubRowStacked}>
                                                <input type="text" className={styles.formInput} placeholder="Android package name, e.g. com.yourapp.android"
                                                    value={btn.packageName} onChange={(e) => updateButton(idx, 'packageName', e.target.value)} />
                                                <input type="text" className={styles.formInput} placeholder="App signature hash"
                                                    value={btn.signatureHash} onChange={(e) => updateButton(idx, 'signatureHash', e.target.value)} />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className={styles.buttonSection}>
                            <button type="button" className={styles.addButtonTrigger} onClick={() => setIsMenuOpen(!isMenuOpen)} disabled={totalButtons >= MAX_TOTAL_BUTTONS}>
                                <Plus size={16} /> Add a button
                            </button>
                            {isMenuOpen && totalButtons < MAX_TOTAL_BUTTONS && (
                                <div className={styles.dropdownMenu}>
                                    {BUTTON_TYPES.map((type) => (
                                        <button key={type} type="button" className={styles.dropdownItem} disabled={!canAddButtonType(type)} onClick={() => addActionBtn(type)}>
                                            {type}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* RIGHT PANEL: Preview */}
                <div className={styles.rightPanel}>
                    <div className={styles.waPreviewMockup}>
                        <div className={styles.waBubble}>
                            {formData.headerType === 'Text' && formData.headerText && <div className={styles.waHeaderTextPreview}>{renderBodyPreview(formData.headerText, headerValueMap)}</div>}

                            {['Image', 'Video', 'Document'].includes(formData.headerType) && (
                                <div className={styles.waHeaderMedia} style={{ padding: formData.headerMediaUrl ? 0 : '40px 20px', backgroundColor: '#e2e8f0', borderRadius: '8px 8px 0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'hidden' }}>
                                    {formData.headerMediaUrl ? (
                                        formData.headerType === 'Image' ? (
                                            <img src={formData.headerMediaUrl} alt="Preview" style={{ width: '100%', display: 'block', objectFit: 'cover' }} />
                                        ) : formData.headerType === 'Video' ? (
                                            <video src={formData.headerMediaUrl} controls style={{ width: '100%', display: 'block', maxHeight: '180px', objectFit: 'cover', backgroundColor: '#000' }} />
                                        ) : (
                                            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                                                <FileText color="#475569" size={36} />
                                                <span style={{ fontSize: '12px', marginTop: '8px', color: '#475569', fontWeight: 600 }}>Document Attached</span>
                                            </div>
                                        )
                                    ) : (
                                        <>
                                            {formData.headerType === 'Image' ? <ImageIcon color="#94a3b8" size={36} /> :
                                                formData.headerType === 'Video' ? <Video color="#94a3b8" size={36} /> :
                                                    <FileText color="#94a3b8" size={36} />}
                                            <span style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
                                                {isEditMode && formData.headerMediaHandle ? 'Loading Saved Media...' : formData.headerType}
                                            </span>
                                        </>
                                    )}
                                </div>
                            )}

                            <div className={styles.waTextContent}>{renderBodyPreview(formData.bodyText, bodyValueMap)}</div>
                            {formData.footerText && <div className={styles.waFooterPreview}>{formData.footerText}</div>}
                            {formData.buttons.length > 0 && (
                                <div className={styles.waBtnStack}>
                                    {formData.buttons.map((btn, idx) => <div key={idx} className={styles.waPreviewBtn}>{btn.text || 'Button'}</div>)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <footer className={styles.bottomBar}>
                <div>{showErrors && !isTemplateValid && <div className={styles.validationSummary}>{validationErrors[0]}</div>}</div>
                <div className={styles.bottomBarActions}>
                    <button type="button" className={styles.btnOutline} onClick={() => navigate('/marketing/templates')}>Cancel</button>
                    <button type="button" className={styles.btnPrimary} onClick={handleSubmit} disabled={(showErrors && !isTemplateValid) || isSubmitting}>
                        {isSubmitting ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Loader2 className={styles.spinner} size={14} /> {isEditMode ? 'Updating...' : 'Submitting...'}</span>
                        ) : (isEditMode ? 'Update Template' : 'Submit for review')}
                    </button>
                </div>
            </footer>
        </div>
    );
};

export default CreateTemplate;